import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchTaxRates } from "@/state/taxRates/taxRate.slice";

const pct = (n) => `${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 3 })}%`;

/**
 * "Applicable Tax" + "Selling Price Tax Type" for the add/edit product forms.
 *
 *   taxSetting: 'default' | 'exempt' | a tax rate/group id (string)
 *   taxType:    'exclusive' | 'inclusive'
 *
 * 'default' stores nothing on the product, so it follows whatever the Tax
 * Rates page marks as default — including later changes.
 */
export default function ProductTaxFields({
  taxSetting = "default",
  taxType = "exclusive",
  onTaxSettingChange,
  onTaxTypeChange,
  disabled = false,
}) {
  const dispatch = useDispatch();
  const { ready, rates, groups, businessRate, loaded, isLoading } = useSelector((state) => state.taxRate);

  useEffect(() => {
    if (!loaded && !isLoading) dispatch(fetchTaxRates());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Before the tax migration the fields would save nothing, so hide them.
  if (loaded && !ready) return null;

  const setting = String(taxSetting || "default");
  const all = [...groups, ...rates];
  const defaultEntry = all.find((e) => e.is_default && e.status === 1);
  const defaultLabel = defaultEntry
    ? `Default — ${defaultEntry.name} (${pct(defaultEntry.rate)})`
    : businessRate > 0
      ? `Default — business rate (${pct(businessRate)})`
      : "Default — no tax set up";

  // Keep a switched-off choice visible, so opening the form never silently changes it.
  const visible = all.filter((e) => e.status === 1 || String(e.id) === setting);
  const visibleGroups = visible.filter((e) => e.is_group);
  const visibleRates = visible.filter((e) => !e.is_group);
  const itemLabel = (e) => `${e.name} (${pct(e.rate)})${e.status !== 1 ? " — off" : ""}`;

  const exempt = setting === "exempt";
  const settingHelp = exempt
    ? "No tax is charged on this product."
    : setting === "default"
      ? "Follows the default on the Tax Rates page, including future changes."
      : "Always charged this tax, whatever the default is.";

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-1.5">
        <Label htmlFor="tax_setting">Applicable Tax</Label>
        <Select value={setting} onValueChange={onTaxSettingChange} disabled={disabled}>
          <SelectTrigger id="tax_setting">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">{defaultLabel}</SelectItem>
            <SelectItem value="exempt">None — tax exempt</SelectItem>
            {visibleGroups.map((g) => (
              <SelectItem key={g.id} value={String(g.id)}>
                Group: {itemLabel(g)}
              </SelectItem>
            ))}
            {visibleRates.map((r) => (
              <SelectItem key={r.id} value={String(r.id)}>
                {itemLabel(r)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {settingHelp}{" "}
          <Link to="/dashboard/settings/tax-rates" className="underline underline-offset-2 hover:text-foreground">
            Manage tax rates
          </Link>
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="tax_type">Selling Price Tax Type</Label>
        <Select value={taxType || "exclusive"} onValueChange={onTaxTypeChange} disabled={disabled || exempt}>
          <SelectTrigger id="tax_type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="exclusive">Exclusive</SelectItem>
            <SelectItem value="inclusive">Inclusive</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {taxType === "inclusive"
            ? "The selling price already includes tax — the customer pays the price shown."
            : "Tax is added on top of the selling price at checkout."}
        </p>
      </div>
    </div>
  );
}
